// EmployeeSyncPanel.jsx (updated - replace the edit modal section)
import React, { useEffect, useState } from 'react'
import { apiGet, apiPost, apiDelete } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import useToastContext from 'hooks/useToastContext'
import helpers from '../../utils/helpers'
import { 
  FaSync, FaCheckCircle, FaExclamationCircle, FaEye, FaTrash, FaEdit,
} from 'react-icons/fa'
import OSearch from 'components/reusable/OSearch'
import DetailModalContent from './DetailModalContent'
import EmployeeEditModal from './EmployeeEditModal' // Import the new component
import { useMFA } from 'context/MFAContext'
import { callWithStepUp } from 'utils/payrollMfa'

const STATUS_BADGE = {
  synced: { icon: FaCheckCircle, cls: 'bg-green-100 text-green-800', label: 'Synced' },
  error: { icon: FaSync, cls: 'bg-yellow-100 text-yellow-800', label: 'Not Synced' },
  not_synced: { icon: FaSync, cls: 'bg-yellow-100 text-yellow-800', label: 'Not Synced' }
}

const EmployeeSyncPanel = () => {
  const notification = useToastContext()
  const { requireMfa } = useMFA()
  const [employees, setEmployees] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [locations, setLocations] = useState([])
  const [syncInProgress, setSyncInProgress] = useState({})
  const [locationPickerFor, setLocationPickerFor] = useState(null)
  const [selectedLocationId, setSelectedLocationId] = useState('')
  const [detailModal, setDetailModal] = useState(null)
  const [editModalEmployee, setEditModalEmployee] = useState(null) // Changed from editModal
  const [validationErrors, setValidationErrors] = useState(null)

  const getEmployees = async () => {
    try {
      const res = await apiGet(apiPath.employeeSyncList, { keyword: searchTerm, pageSize: 50 })
      if (res?.data?.success) setEmployees(res.data.results.docs || [])
    } catch (err) {
      console.log('Error fetching employee sync list:', err.message)
    }
  }

  const getLocations = async () => {
    try {
      const res = await apiGet(apiPath.getLocations)
      if (res?.data?.success) setLocations(res.data.results || [])
    } catch (err) {
      console.log('Error fetching locations:', err.message)
    }
  }

  useEffect(() => { getEmployees() }, [searchTerm])
  useEffect(() => { getLocations() }, [])

  const startSync = (employee) => {
    if (!employee.everee?.workLocationId) {
      setLocationPickerFor(employee._id)
      setSelectedLocationId('')
      return
    }
    doSync(employee._id)
  }

  const confirmLocationAndSync = async () => {
    if (!selectedLocationId) { notification.error('Pick a work location first'); return }
    await doSync(locationPickerFor, selectedLocationId)
    setLocationPickerFor(null)
  }

  // const doSync = async (employeeId, workLocationId) => {
  //   setSyncInProgress(prev => ({ ...prev, [employeeId]: true }))
  //   setValidationErrors(null)
    
  //   try {
  //     const res = await apiPost(`${apiPath.employeeSync}/${employeeId}/sync`, workLocationId ? { workLocationId } : {})
      
  //     if (res?.data?.success) {
  //       notification.success(res.data.results?.note || 'Employee synced to Everee')
  //       getEmployees()
  //     } else {
  //       if (res?.data?.message === 'EMPLOYEE_VALIDATION_FAILED') {
  //         const { missingFields, errors, suggestion } = res.data.results || {}
  //         setValidationErrors({
  //           employeeId,
  //           missingFields,
  //           errors,
  //           suggestion
  //         })
  //         notification.error('Employee validation failed - see details')
  //       } else {
  //         notification.error(res?.data?.message || 'Sync failed')
  //       }
  //     }
  //   } catch (err) {
  //     notification.error('Something went wrong')
  //   } finally {
  //     setSyncInProgress(prev => ({ ...prev, [employeeId]: false }))
  //   }
  // }
    const doSync = async (employeeId, workLocationId) => {
    setSyncInProgress(prev => ({ ...prev, [employeeId]: true }))
    setValidationErrors(null)

    try {
      const res = await callWithStepUp(requireMfa, (extra) =>
        apiPost(`${apiPath.employeeSync}/${employeeId}/sync`, {
          ...(workLocationId ? { workLocationId } : {}),
          ...extra
        })
      )
      if (!res) {
        notification.error('Verification cancelled')
        return
      }

      if (res?.data?.success) {
        notification.success(res.data.results?.note || 'Employee synced to Everee')
        getEmployees()
      } else {
        if (res?.data?.message === 'EMPLOYEE_VALIDATION_FAILED') {
          const { missingFields, errors, suggestion } = res.data.results || {}
          setValidationErrors({
            employeeId,
            missingFields,
            errors,
            suggestion
          })
          notification.error('Employee validation failed - see details')
        } else {
          notification.error(res?.data?.message || 'Sync failed')
        }
      }
    } catch (err) {
      notification.error('Something went wrong')
    } finally {
      setSyncInProgress(prev => ({ ...prev, [employeeId]: false }))
    }
  }

  const viewDetail = async (employee) => {
    try {
      const res = await apiGet(`${apiPath.employeeSync}/${employee._id}/sync`)
      if (res?.data?.success) {
        setDetailModal({
          employee: employee,
          evereeData: res.data.results.evereeDetail
        })
      } else {
        notification.error(res?.data?.message || 'Could not load Everee detail')
      }
    } catch (err) {
      notification.error('Something went wrong')
    }
  }

  const openEditModal = (employee) => {
    setEditModalEmployee(employee) // Set the employee to edit
  }

  // const deactivate = (employee) => {
  //   helpers.alertFunction(
  //     `Deactivate ${employee.name}? If they have pay history in Everee, only the local record is deactivated — Everee's record stays for compliance.`,
  //     employee._id,
  //     async (id) => {
  //       try {
  //         const res = await apiDelete(`${apiPath.employeeSync}/${id}/sync`)
  //         if (res?.data?.success) {
  //           notification.success(res.data.results?.message || 'Deactivated')
  //           getEmployees()
  //         } else {
  //           notification.error(res?.data?.message || 'Failed')
  //         }
  //       } catch (err) {
  //         notification.error('Something went wrong')
  //       }
  //     },
  //     true
  //   )
  // }
    const deactivate = (employee) => {
    helpers.alertFunction(
      `Deactivate ${employee.name}? If they have pay history in Everee, only the local record is deactivated — Everee's record stays for compliance.`,
      employee._id,
      async (id) => {
        try {
          const res = await callWithStepUp(requireMfa, (extra) =>
            apiDelete(`${apiPath.employeeSync}/${id}/sync`, { ...extra })
          )
          if (!res) {
            notification.error('Verification cancelled')
            return
          }
          if (res?.data?.success) {
            notification.success(res.data.results?.message || 'Deactivated')
            getEmployees()
          } else {
            notification.error(res?.data?.message || 'Failed')
          }
        } catch (err) {
          notification.error('Something went wrong')
        }
      },
      true
    )
  }

  const badge = (status, syncError = null) => {
    const b = STATUS_BADGE[status] || STATUS_BADGE.not_synced
    const Icon = b.icon
    return (
      <span title={syncError || ""} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${b.cls}`}>
        <Icon className='mr-1 w-3 h-3' />{b.label}
      </span>
    )
  }

  return (
    <div className='p-4'>
      <div className='flex relative items-center justify-between mb-4'>
        <h4 className='text-sm font-semibold text-gray-700'>Employees ({employees.length})</h4>
        <OSearch placeholder='Search by name' searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
      </div>

      <div className='border rounded-lg overflow-hidden'>
        <table className='w-full text-xs text-left text-[#A5A5A5]'>
          <thead className='text-gray-900 text-xs border-b bg-[#E1E6EE]'>
            <tr>
              <th className='px-4 py-2'>Name</th>
              <th className='px-4 py-2'>Employee ID</th>
              <th className='px-4 py-2'>Pay Type</th>
              <th className='px-4 py-2 text-center'>Everee Status</th>
              <th className='px-4 py-2 text-center'>Action</th>
            </tr>
          </thead>
          <tbody>
            {employees.map(emp => (
              <tr key={emp._id} className='bg-white border-b hover:bg-gray-50'>
                <td className='px-4 py-3 text-gray-900 font-medium'>{emp.name}</td>
                <td className='px-4 py-3 text-gray-600'>{emp.employeeId}</td>
                <td className='px-4 py-3 text-gray-600 capitalize'>{emp.wageDetails?.payType || 'hourly'}</td>
                <td className='px-4 py-3 text-center'>
                  <div className='flex flex-col items-center gap-1'>
                    {badge(emp.everee?.onboardingStatus, emp?.everee?.syncError)}
                    {/* {emp.everee?.syncError && (
                      <span className='text-[10px] text-red-500' title={emp.everee.syncError}>
                        {emp.everee.syncError.slice(0, 40)}{emp.everee.syncError.length > 40 ? '…' : ''}
                      </span>
                    )} */}
                  </div>
                </td>
                <td className='px-4 py-3 text-center'>
                  <div className='flex justify-center gap-2'>
                    {emp.everee?.onboardingStatus === 'synced' ? (
                      <>
                        <button 
                          type='button' 
                          onClick={() => viewDetail(emp)} 
                          className='p-1.5 hover:bg-blue-50 rounded text-blue-600' 
                          title='View Everee Details'
                        >
                          <FaEye className='w-3 h-3' />
                        </button>
                        <button 
                          type='button' 
                          onClick={() => openEditModal(emp)} 
                          className='p-1.5 hover:bg-yellow-50 rounded text-yellow-600' 
                          title='Update in Everee'
                        >
                          <FaEdit className='w-3 h-3' />
                        </button>
                        <button 
                          type='button' 
                          onClick={() => deactivate(emp)} 
                          className='p-1.5 hover:bg-red-50 rounded text-red-600' 
                          title='Deactivate'
                        >
                          <FaTrash className='w-3 h-3' />
                        </button>
                      </>
                    ) : (
                      <button
                        type='button'
                        onClick={() => startSync(emp)}
                        disabled={syncInProgress[emp._id]}
                        className='px-3 py-1 bg-[#4F46E5] text-white rounded text-xs hover:bg-[#4338CA] disabled:opacity-50'
                      >
                        {syncInProgress[emp._id] ? 'Syncing…' : 'Sync to Everee'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {employees.length === 0 && (
              <tr><td colSpan={5} className='text-center py-8 text-gray-400'>No employees found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ─── Work location picker ─── */}
      {locationPickerFor && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'>
          <div className='bg-white rounded-lg p-6 w-96'>
            <h4 className='font-semibold mb-3'>Assign a Work Location</h4>
            <p className='text-xs text-gray-500 mb-3'>Required before this employee can be synced to Everee — determines their tax jurisdiction.</p>
            <select
              value={selectedLocationId}
              onChange={(e) => setSelectedLocationId(e.target.value)}
              className='w-full border rounded-md px-3 py-2 mb-4'
            >
              <option value=''>Select a location</option>
              {locations.map(loc => (
                <option key={loc._id} value={loc.evereeId || loc._id}>{loc.name}</option>
              ))}
            </select>
            <div className='flex justify-end gap-2'>
              <button type='button' onClick={() => setLocationPickerFor(null)} className='px-4 py-2 text-sm bg-gray-200 rounded-md'>Cancel</button>
              <button type='button' onClick={confirmLocationAndSync} className='px-4 py-2 text-sm bg-[#4F46E5] text-white rounded-md'>Sync</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Everee worker detail modal ─── */}
      {detailModal && (
        <DetailModalContent 
          data={detailModal} 
          onClose={() => setDetailModal(null)} 
        />
      )}

      {/* ─── Employee Edit Modal ─── */}
      {editModalEmployee && (
        <EmployeeEditModal
          employee={editModalEmployee}
          onClose={() => setEditModalEmployee(null)}
          onSuccess={() => {
            getEmployees()
            notification.success('Employee updated successfully')
          }}
        />
      )}

      {/* ─── Validation Errors Modal ─── */}
      {validationErrors && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'>
          <div className='bg-white rounded-lg p-6 w-[600px] max-h-[80vh] overflow-y-auto'>
            <div className='flex justify-between items-center mb-4'>
              <h4 className='font-semibold text-red-600'>Validation Failed</h4>
              <button type='button' onClick={() => setValidationErrors(null)} className='text-gray-400 text-2xl leading-none'>×</button>
            </div>
            
            <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded'>
              <p className='text-sm text-red-800'>{validationErrors.suggestion}</p>
            </div>
            
            {validationErrors.missingFields?.length > 0 && (
              <div className='mb-4'>
                <h5 className='font-semibold text-sm text-gray-700 mb-2'>Missing Required Fields:</h5>
                <ul className='list-disc pl-5 space-y-1'>
                  {validationErrors.missingFields.map((field, index) => (
                    <li key={index} className='text-sm text-red-600'>{field}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {validationErrors.errors?.length > 0 && (
              <div>
                <h5 className='font-semibold text-sm text-gray-700 mb-2'>Validation Errors:</h5>
                <ul className='list-disc pl-5 space-y-1'>
                  {validationErrors.errors.map((error, index) => (
                    <li key={index} className='text-sm text-orange-600'>{error}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className='flex justify-end mt-6'>
              <button 
                type='button' 
                onClick={() => setValidationErrors(null)} 
                className='px-4 py-2 bg-[#4F46E5] text-white rounded-md text-sm'
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmployeeSyncPanel